import React from "react";
import styled from "styled-components";
import type { Alignment, IRef } from "@blueprintjs/core";
import { Classes, ControlGroup } from "@blueprintjs/core";
import type { ComponentProps } from "widgets/BaseComponent";
import moment from "moment-timezone";
import "@blueprintjs/datetime/lib/css/blueprint-datetime.css";
import type { TextSize } from "constants/WidgetConstants";
import { Colors } from "constants/Colors";
import { LabelPosition } from "components/constants";
import { lightenColor, PopoverStyles } from "widgets/WidgetUtils";
import LabelWithTooltip, { labelLayoutStyles, } from "widgets/components/LabelWithTooltip";
import { TimePicker } from "antd";
import { Input } from "antd";
import { FontStyleTypes } from "@design-system/widgets-old";
import { getAntdDatePickerPopupStyled } from "widgets/widgetStyled";

const DATEPICKER_POPUP_CLASSNAME = "datepickerwidget-popup";
const RangeTimePickerWrapper = styled(TimePicker.RangePicker) <any>`
  input {
    border: none !important;
  }
`

const StyledControlGroup = styled(ControlGroup) <{
  compactMode: boolean;
  labelPosition?: LabelPosition;
  borderRadius: string;
  boxShadow?: string;
  accentColor: string;
  isReadOnly: boolean;
  it_color?: string;
  it_size?: string;
  it_style?: string;
  ib_bgColor?: string;
  ib_borderColor?: string;
  disabled?: boolean;
}>`
  ${labelLayoutStyles}
  /**
    When the label is on the left it is not center aligned
    here set height to auto and not 100% because the input
    has fixed height and stretch the container.
  */
    ${({ labelPosition }) => {
    if (labelPosition === LabelPosition.Left) {
      return `
          height: auto !important;
          align-items: stretch;
        `;
    }
  }}

  &&& {
     .ant-picker-input > input {
      font-size: ${({ it_size }) => {
    return (it_size ? `${it_size}` : "14px") + ' !important'
  }};
     font-style: ${({ it_style }) => {
    return (it_style?.includes(FontStyleTypes.ITALIC) ? "italic" : "") + ' !important'
  }};
     font-weight: ${({ it_style }) => {
    return (it_style?.includes(FontStyleTypes.BOLD) ? "bold" : "") + ' !important'
  }};
       ${({ it_color }) => {
    if (it_color) {
      return `color:${it_color}`;
    }
  }}
     }

    .ant-picker {
      color: ${({ it_color }) => it_color ? it_color : 'var(--wds-color-text)'} !important;
      background:  ${({ disabled, ib_bgColor }) => {
    return ib_bgColor ? ib_bgColor : 'var(--wds-color-bg)'
  }} !important;
      border-radius: ${({ borderRadius }) => borderRadius} !important;
      box-shadow: ${({ boxShadow }) => `${boxShadow}`} !important;
      border: 1px solid;
      border-color: ${({ ib_borderColor }) => {
    return ib_borderColor ? ib_borderColor : `var(--wds-color-border);`
  }} !important;
      width: 100%;
      height: 100%;
      min-height: 32px;
      align-items: center;
      transition: none;

      &:active:not(:disabled) {
        border-color: ${({ accentColor }) => accentColor};
      }

      &:hover:not(:disabled) {
        border-color: ${({ }) => `var(--wds-color-border-hover)`};
      }

      &:focus:not(:disabled) {
        outline: 0;
        border: 1px solid;
        border-color: ${({ accentColor }) => accentColor};
        box-shadow: ${({ accentColor }) => `0px 0px 0px 2px ${lightenColor(accentColor)} !important;`};
      }

      &:focus-within {
        border-color: ${({ accentColor }) => accentColor} !important;
        box-shadow: ${({ accentColor }) => `0px 0px 0px 2px ${lightenColor(accentColor)} !important;`};
      }
    }

    .ant-picker.ant-picker-disabled {
      background: var(--wds-color-bg-disabled) !important;
      color: var(--wds-color-text-disabled) !important;
      border-color: var(--wds-color-border-disabled)  !important;
    }

    .${Classes.INPUT}:disabled {
      background: var(--wds-color-bg-disabled) !important;
      color: var(--wds-color-text-disabled) !important;
      border-color: var(--wds-color-border-disabled)  !important;
    }

    .${Classes.INPUT}:not(:disabled)::placeholder {
      color: var(--wds-color-text-light);
    }

    .${Classes.INPUT}::placeholder {
      color: var(--wds-color-text-disabled-light);
    }

    .${Classes.INPUT_GROUP} {
      display: block;
      margin: 0;
    }

    .${Classes.CONTROL_GROUP} {
      justify-content: flex-start;
    }
  }
  &&& {
    input {
    ${({ isReadOnly }) => {
    if (!isReadOnly) {
      return `
            border: 1px solid;
            border-color: ${() => Colors.HIT_GRAY};
            box-shadow: none;
           `;
    }
  }}
    }
  }
`;

export const RangeTimeInputWrapper = styled.div<{
  compactMode: boolean;
  labelPosition?: LabelPosition;
}>`
  display: flex;
  &&& {
    flex-grow: 0;
  }
  width: 100%;
`;

class RangeTimePickerComponent extends React.Component<RangeTimePickerComponentProps,
  RangeTimePickerComponentState> {
  constructor(props: RangeTimePickerComponentProps) {
    super(props);
    this.state = {
      selectedDate: props.selectedDate,
    };
  }

  componentDidUpdate(prevProps: RangeTimePickerComponentProps) {
    if (
      this.props.selectedDate !== this.state.selectedDate
    ) {
      this.setState({ selectedDate: this.props.selectedDate });
    }
  }

  getDisabledTimes = (minTime: any, maxTime: any) => {
    const defaultMin = '00:00:00';
    const defaultMax = '23:59:59';
    const parseTime = (timeStr: any) => {
      if (!timeStr) return null;
      const [hours, minutes, seconds] = timeStr.split(':').map(Number);
      return { hours, minutes, seconds };
    };
    const min: any = parseTime(minTime) || parseTime(defaultMin);
    const max: any = parseTime(maxTime) || parseTime(defaultMax);
    const getLessThan = (value: any, maxRange: any) => {
      return Array.from({ length: value }, (_, i) => i);
    };
    const getGreaterThan = (value: any, maxRange: any) => {
      return Array.from({ length: maxRange - value - 1 }, (_, i) => i + value + 1);
    };

    const disabledHours = () => {
      const hours = [];
      if (minTime) {
        hours.push(...getLessThan(min.hours, 24));
      }
      if (maxTime) {
        hours.push(...getGreaterThan(max.hours, 24));
      }
      return [...new Set(hours)];
    };

    const disabledMinutes = (selectedHour: any) => {
      const minutes = [];
      if (minTime && selectedHour <= min.hours) {
        if (selectedHour === min.hours) {
          minutes.push(...getLessThan(min.minutes, 60));
        } else {
          return Array.from({ length: 60 }, (_, i) => i);
        }
      }
      if (maxTime && selectedHour >= max.hours) {
        if (selectedHour === max.hours) {
          minutes.push(...getGreaterThan(max.minutes, 60));
        } else {
          return Array.from({ length: 60 }, (_, i) => i);
        }
      }
      return [...new Set(minutes)];
    };

    const disabledSeconds = (selectedHour: any, selectedMinute: any) => {
      const seconds = [];
      if (minTime && selectedHour <= min.hours) {
        if (selectedHour === min.hours && selectedMinute <= min.minutes) {
          if (selectedMinute === min.minutes) {
            seconds.push(...getLessThan(min.seconds, 60));
          } else {
            return Array.from({ length: 60 }, (_, i) => i);
          }
        }
      }
      if (maxTime && selectedHour >= max.hours) {
        if (selectedHour === max.hours && selectedMinute >= max.minutes) {
          if (selectedMinute === max.minutes) {
            seconds.push(...getGreaterThan(max.seconds, 60));
          } else {
            return Array.from({ length: 60 }, (_, i) => i);
          }
        }
      }
      return [...new Set(seconds)];
    };

    return {
      disabledHours,
      disabledMinutes,
      disabledSeconds
    };
  }
  render() {
    const {
      compactMode,
      isDisabled,
      isLoading,
      isRequired,
      labelAlignment,
      labelPosition,
      labelStyle,
      labelText,
      labelTextColor,
      labelTextSize,
      labelTooltip,
      labelWidth,
      placeholderText1,
      placeholderText2,
      dateFormat,
      selectedDate,
      it_color,
      it_size,
      it_style,
      ib_bgColor,
      ib_borderColor,
      accentColor,
    } = this.props;

    const yyyyMMdd = "2000-01-01";
    const getTimeRangeValue = (val: any) => {
      try {
        let res: any = null;
        if (val) {
          let list = val.split("~");
          if (list.length < 2) return [];
          res = [moment(`${yyyyMMdd} ${list[0]}`), moment(`${yyyyMMdd} ${list[1]}`)]
        }
        return res;
      } catch (errorInfo) {
        console.warn(errorInfo);
        return [];
      }
    }

    const disabledTime = () => {
      const min = this.props.minDate;
      const max = this.props.maxDate;
      return this.getDisabledTimes(min, max)
    };

    const dynamicStyles = `
      .datePicker2Widget__popover {
        ${getAntdDatePickerPopupStyled(accentColor)}
      }
    `;

    return (
      <StyledControlGroup
        accentColor={this.props.accentColor}
        borderRadius={this.props.borderRadius}
        boxShadow={this.props.boxShadow}
        compactMode={this.props.compactMode}
        data-testid="datepicker-container"
        fill
        isReadOnly={this.props.isReadonly}
        disabled={isDisabled}
        labelPosition={this.props.labelPosition}
        it_color={it_color}
        it_size={it_size}
        it_style={it_style}
        ib_bgColor={ib_bgColor}
        ib_borderColor={ib_borderColor}
      >
        {labelText && (
          <LabelWithTooltip
            alignment={labelAlignment}
            className={`datepicker-label`}
            color={labelTextColor}
            compact={compactMode}
            cyHelpTextClassName="datepicker-tooltip"
            disabled={isDisabled}
            fontSize={labelTextSize}
            fontStyle={labelStyle}
            helpText={labelTooltip}
            isDynamicHeightEnabled={this.props.isDynamicHeightEnabled}
            loading={isLoading}
            position={labelPosition}
            text={labelText}
            width={labelWidth}
            isRequired={isRequired}
          />
        )}
        {
          this.props.isReadonly ? (
            <Input readOnly={true} bordered={false} value={this.state.selectedDate}
              style={{
                color: it_color || "#000000",
                backgroundColor: "transparent",
                fontSize: it_size,
                padding: 0,
                fontWeight: it_style?.includes(FontStyleTypes.BOLD) ? "bold" : "",
                fontStyle: it_style?.includes(FontStyleTypes.ITALIC) ? "italic" : "",
                borderRadius: this.props.borderRadius,
              }}
            />) : <RangeTimeInputWrapper
              compactMode={compactMode}
              labelPosition={labelPosition}
            >
            <style>{dynamicStyles}</style>
            <RangeTimePickerWrapper
              popupClassName="datePicker2Widget__popover"
              style={{ width: "100%" }}
              value={getTimeRangeValue(this.state.selectedDate)}
              onChange={(date: any, dateString: any) => this.onChange(date, dateString, dateFormat)}
              format={dateFormat}
              disabled={isDisabled}
              placeholder={isDisabled ? ["", ""] : [placeholderText1, placeholderText2]}
              disabledTime={disabledTime}
            />
          </RangeTimeInputWrapper>
        }
        <PopoverStyles
          accentColor={this.props.accentColor}
          borderRadius={this.props.borderRadius}
          portalClassName={`${DATEPICKER_POPUP_CLASSNAME}-${this.props.widgetId}`}
        />
      </StyledControlGroup>
    );
  }

  onChange = (date: any, timeString: any, dateFormat: any) => {
    debugger;
    let timeStr = (timeString[0]==""&&timeString[1]=="")?"":`${timeString[0]}~${timeString[1]}`;
    this.setState({
      selectedDate: timeStr,
    });
    const { onChange } = this.props;
    onChange(timeStr)
  };
}

interface RangeTimePickerComponentProps extends ComponentProps {
  compactMode: boolean;
  labelText: string;
  labelPosition?: LabelPosition;
  labelAlignment?: Alignment;
  labelWidth?: number;
  labelTextColor?: string;
  labelTextSize?: TextSize;
  labelStyle?: string;
  dateFormat: string;
  selectedDate?: string;
  minDate?: string;
  maxDate?: string;
  timezone?: string;
  isDisabled: boolean;
  isReadonly: boolean;
  isDynamicHeightEnabled?: boolean;
  onChange: (selectedDate: string) => void;
  isLoading: boolean;
  withoutPortal?: boolean;
  inputRef?: IRef<HTMLInputElement>;
  borderRadius: string;
  boxShadow?: string;
  accentColor: string;
  labelTooltip?: string;
  onPopoverClosed?: (e: unknown) => void;
  isPopoverOpen?: boolean;
  isRequired?: boolean;
  placeholderText1?: string;
  placeholderText2?: string;
}

interface RangeTimePickerComponentState {
  selectedDate?: string;
}

export default RangeTimePickerComponent;
